'use client';

import { FacetBreadcrumbs } from './FacetBreadcrumbs';
import { HorizontalFilters } from './HorizontalFilters';
import { MobileFilterBar } from './MobileFilterBar';
import { Pager } from './Pager';
import { QuerySummary } from './QuerySummary';
import { ResultList } from './ResultList';
import { SearchSurfaceProvider } from './SearchSurfaceContext';
import { Sidebar } from './Sidebar';
import { useSearchControllers } from './headless/SearchDefinitionContext';

type SearchSsrLayoutProps = {
  resultSectionTestId: string;
  /**
   * Listing surface: popularity-default sort with no Relevance option. Seeded
   * by the page (not inferred from the route) so sort stays decoupled from the
   * pathname — see SearchSurfaceContext.
   */
  isListing?: boolean;
};

/**
 * SSR results layout for both surfaces (search and listing — the listing page
 * is the same experience filtered to a category). Desktop composition:
 * query summary on top, facet sidebar (Sale / Category / Ownership) on the
 * left, and a main column with the horizontal filter bar
 * (Price / Rating / Publisher / Release date + sort + page size), the
 * breadcrumb chips, the result grid, and the pager. Below `lg` the sidebar and
 * horizontal bar give way to the mobile filter bar, whose drawer holds the
 * same facets plus sort and page size.
 */
export const SearchSsrLayout = ({
  resultSectionTestId,
  isListing = false,
}: SearchSsrLayoutProps) => {
  // Deterministic hydration marker for e2e: the generated hooks expose
  // `methods` only once SearchEngineProvider's hydrateStaticState has resolved
  // and the hydrated controllers are in the StateProvider — the moment sort/
  // pager/page-size/facet clicks stop being silent no-ops. React DOM hydration
  // alone is NOT that moment (local UI state responds well before the engine
  // does), so tests must wait on this attribute, not on interactive-looking
  // behavior.
  const { methods: sortMethods } = useSearchControllers().useSort();
  const isEngineHydrated = Boolean(sortMethods);

  return (
    <SearchSurfaceProvider isListing={isListing}>
      <div
        data-hydrated={isEngineHydrated ? 'true' : undefined}
        className="container min-h-[25rem] pb-10 pt-4 lg:max-w-full lg:px-8 lg:pb-16 lg:pt-12 xl:max-w-full 2xl:max-w-none"
      >
        {/* The app shell already provides the page's <main id="main"> landmark. */}
        <QuerySummary variant="title" className="header-large mb-1" />
        <QuerySummary
          variant="count"
          className="body-small mb-4 text-grey-600 lg:mb-6"
        />
        <div className="flex flex-col lg:flex-row lg:gap-5">
          <aside className="hidden w-[304px] min-w-[304px] shrink-0 lg:block">
            <Sidebar />
          </aside>

          <div className="min-w-0 flex-1">
            <MobileFilterBar className="mb-3 lg:hidden" />
            <HorizontalFilters className="hidden lg:flex" />
            {/* One chips row for both surfaces: the mobile bar sits directly
                above it, so a second (breakpoint-hidden) instance would only
                duplicate the markup and its breadcrumb subscription. */}
            <FacetBreadcrumbs className="mt-3" />
            <div className="mt-4 lg:mt-5" data-test={resultSectionTestId}>
              <ResultList />
            </div>
            <div className="mt-4 flex justify-center lg:mt-5">
              <Pager />
            </div>
          </div>
        </div>
      </div>
    </SearchSurfaceProvider>
  );
};
